/** Browser-safe Lynx Study Session helpers. Do not import Prisma or Node APIs here. */

export const MIN_STREAK_SEC = 60;
export const DEFAULT_GOAL_MINUTES = 45;
export const MAX_FOCUS_MINUTES = 12 * 60;

export function clampFocusMinutes(minutes: number) {
  return Math.max(1, Math.min(MAX_FOCUS_MINUTES, Math.round(minutes) || 1));
}

/** Journey chips for Lynx Study Session setup — journeys first, then the legacy certification column. */
export function certNamesFromProfile(profile: {
  certification?: string | null;
  certificationJourneys?: unknown;
} | null | undefined): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  const add = (value: unknown) => {
    const name = String(value || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(name);
  };
  const journeys = profile?.certificationJourneys;
  if (Array.isArray(journeys)) {
    for (const row of journeys) {
      if (row && typeof row === "object") add((row as { certification?: unknown }).certification);
    }
  }
  for (const part of String(profile?.certification || "").split(",")) add(part);
  return names;
}

export function formatPlanLength(minutes: number) {
  const value = Math.max(0, Math.round(minutes));
  if (value >= 60 && value % 60 === 0) {
    const hours = value / 60;
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  if (value > 60) {
    const hours = Math.floor(value / 60);
    const mins = value % 60;
    return `${hours}h ${mins}m`;
  }
  return `${value} minute${value === 1 ? "" : "s"}`;
}

export function formatFocusDuration(totalSec: number): string {
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
  return parts.join(" ");
}

export function formatFocusDurationShort(totalSec: number): string {
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export const PLAN_PROGRESS_HELP = {
  title: "What this progress bar means",
  body: [
    "This bar is completed Lynx Study Session time on this plan, divided by the hours you assigned to the plan’s topics. It stops at 100%. Checking off calendar days does not move it.",
    "To improve it, start a Lynx Study Session from this plan, choose a topic that is on the plan, and finish the session. Every completed second on a linked session counts toward the bar. If the bar stays at 0%, add topics and hours to the plan, then study those topics in a Lynx Study Session. Sessions started as “study anyway” are not linked to the plan and do not count.",
  ],
};
